// ─── SuiviTimeline.jsx ──────────────────────────────────────────────────
// Refonte Suivi (2026-07) — frise VERTICALE unifiée « La vie du suivi ».
//
// Journal chronologique UNIQUE du suivi : fusionne les événements jadis
// éparpillés entre la frise horizontale (SuiviCockpitTimeline), le bloc
// Consultations, le bloc Derniers ressentis, le bloc Suivi du poids et
// l'Historique des versions. Chaque info vit ICI et nulle part ailleurs.
//
// Types d'événements (mêmes sources que les marqueurs de l'ancienne frise) :
//   · delivery       — livraison du programme (packStartedAt confirmé)
//   · consultation   — consultationsLog (n°, notes en sous-ligne)
//   · version        — versions du protocole (V1 initiale, V2+ adaptations)
//   · publication    — tampon client_app_publication.published_at par version
//   · phase          — transitions de phase (protocol_phases.completed_at)
//   · feedback       — ressentis GROUPÉS par semaine (comme l'ancienne frise)
//   · weight         — jalon poids hebdomadaire (dernière pesée de la semaine)
//   · pub-pending / pub-scheduled — buildPublicationMarkers (parent)
//
// Composant PRÉSENTATIONNEL PUR : aucune règle clinique, aucun fetch.
// Il reçoit des données déjà calculées par StepFollowup et les ordonne
// anti-chronologiquement (récent en haut). Les seules actions embarquées
// réutilisent des handlers existants du parent :
//   - « Voir » sur une version  → onPreviewVersion (PlanVersionPreviewModal)
//   - « Annuler » sur la dernière consultation → onRemoveLastConsultation

import { useMemo } from 'react';
import { readExactAppointmentV2 } from '../services/appointmentEvidence';

const DAY = 86400000;
const SHA256_RE = /^[a-f0-9]{64}$/;

function toTs(value) {
  if (!value) return null;
  const t = Date.parse(value);
  return Number.isFinite(t) ? t : null;
}

/** Lundi 00:00 de la semaine contenant ts (groupage ressentis / poids). */
function weekStart(ts) {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  const day = (d.getDay() + 6) % 7; // lundi = 0
  return d.getTime() - day * DAY;
}

function fmtDay(ts) {
  const d = new Date(ts);
  const opts = d.getFullYear() === new Date().getFullYear()
    ? { day: '2-digit', month: 'short' }
    : { day: '2-digit', month: 'short', year: '2-digit' };
  return d.toLocaleDateString('fr-CH', opts);
}

function periodBounds(period) {
  if (period == null) return null;
  const from = toTs(period?.from);
  const to = toTs(period?.to);
  return from != null && to != null && from <= to ? { from, to } : false;
}

function cycleReviewKey(review) {
  return review?.id && Number.isInteger(review?.step_number) && review?.template_type
    ? `${review.id}:${review.step_number}:${review.template_type}`
    : null;
}

// Poids de tri intra-jour : à date égale, l'événement le plus « décisionnel »
// remonte (publication au-dessus de la création de version, etc.).
const KIND_RANK = {
  'pub-pending': 9,
  'pub-scheduled': 9,
  publication: 8,
  version: 7,
  phase: 6,
  consultation: 5,
  'cycle-review': 4,
  messages: 4,
  meals: 3,
  weight: 3,
  feedback: 2,
  delivery: 0,
};

// ─── Pictos SVG discrets (12px, monochromes via currentColor) ────────────

function Glyph({ kind, delta }) {
  const common = {
    width: 11,
    height: 11,
    viewBox: '0 0 12 12',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.4,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };
  switch (kind) {
    case 'delivery':
      return (
        <svg {...common}>
          <rect x="1.8" y="3" width="8.4" height="6.6" rx="1" />
          <path d="M1.8 5.4h8.4M6 3v2.4" />
        </svg>
      );
    case 'phase':
      return (
        <svg {...common}>
          <path d="M3.4 10.6V1.8h5.4L7.2 3.9l1.6 2.1H3.4" />
        </svg>
      );
    case 'version':
      return (
        <svg {...common}>
          <rect x="3.3" y="3.3" width="5.4" height="5.4" transform="rotate(45 6 6)" />
        </svg>
      );
    case 'publication':
      return (
        <svg {...common}>
          <rect x="3.3" y="3.3" width="5.4" height="5.4" transform="rotate(45 6 6)" fill="currentColor" />
        </svg>
      );
    case 'pub-pending':
    case 'pub-scheduled':
      return (
        <svg {...common}>
          <circle cx="6" cy="6" r="4.4" />
          <path d="M6 3.6V6l1.7 1.1" />
        </svg>
      );
    case 'cycle-review':
      return (
        <svg {...common}>
          <rect x="2" y="1.8" width="8" height="8.4" rx="1" />
          <path d="M4 4.2h4M4 6.2h4M4 8.2h2.3" />
        </svg>
      );
    case 'feedback':
      return (
        <svg {...common} stroke="none">
          <circle cx="2.8" cy="6" r="1.25" fill="currentColor" />
          <circle cx="6" cy="6" r="1.25" fill="currentColor" />
          <circle cx="9.2" cy="6" r="1.25" fill="currentColor" />
        </svg>
      );
    case 'messages':
      return (
        <svg {...common}>
          <path d="M2 2.4h8v5.4H5.2L2.4 9.9V7.8H2z" />
        </svg>
      );
    case 'meals':
      return (
        <svg {...common}>
          <circle cx="6" cy="6" r="3.7" />
          <path d="M1.2 2.2v3.1M2.2 2.2v3.1M1.2 4.2h1M10.2 2.2v7.6" />
        </svg>
      );
    case 'weight':
      if (typeof delta === 'number' && delta > 0) {
        return (
          <svg {...common}>
            <path d="M2.6 8l3.4-4 3.4 4" />
          </svg>
        );
      }
      if (typeof delta === 'number' && delta < 0) {
        return (
          <svg {...common}>
            <path d="M2.6 4l3.4 4 3.4-4" />
          </svg>
        );
      }
      return (
        <svg {...common}>
          <path d="M2.6 6h6.8" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <circle cx="6" cy="6" r="2" fill="currentColor" stroke="none" />
        </svg>
      );
  }
}

// ─── Composant ────────────────────────────────────────────────────────────

export default function SuiviTimeline({
  deliveredAt = null,
  initialAppointmentAt = null,
  initialAppointmentCompleted = false,
  consultationsLog = [],
  feedbacks = [],
  cycleReviews = [],
  versions = [],
  weightEntries = [],
  protocolPhases = null,
  publicationMarkers = [],
  activePlanPublication = null,
  longitudinalEvents = [],
  period = null,
  onPreviewVersion = null,
  onRemoveLastConsultation = null,
  busy = false,
}) {
  const events = useMemo(() => {
    const out = [];
    const list = Array.isArray(versions) ? versions : [];
    const longitudinalRows = Array.isArray(longitudinalEvents) ? longitudinalEvents : [];
    const linkedCycleReviews = new Set(
      (Array.isArray(consultationsLog) ? consultationsLog : [])
        .map((consultation) => readExactAppointmentV2(consultation?.clinical?.appointment)?.cycleReview)
        .map(cycleReviewKey)
        .filter(Boolean),
    );
    const dependentAppointmentShas = new Set(list.flatMap((version) => {
      const provenance = version?.followupData?._versionProvenance
        || version?.followup_data?._versionProvenance;
      return provenance?.kind === 'followup_manual_version'
        && ['reviewed_appointment_adaptation', 'phase_transition'].includes(provenance?.trigger)
        && SHA256_RE.test(String(provenance?.appointmentSha256 || ''))
        ? [provenance.appointmentSha256]
        : [];
    }));

    // Livraison (même condition que l'ancien marqueur M0 : pack confirmé).
    const deliveryTs = toTs(deliveredAt);
    if (deliveryTs != null) {
      out.push({ id: 'delivery', kind: 'delivery', ts: deliveryTs, label: 'Programme livré' });
    }

    const initialAppointmentTs = initialAppointmentCompleted
      ? toTs(initialAppointmentAt)
      : null;
    if (initialAppointmentTs != null) {
      out.push({
        id: 'consult-initial',
        kind: 'consultation',
        ts: initialAppointmentTs,
        num: 1,
        label: 'Rendez-vous initial',
        sub: 'Début de l’accompagnement',
        appointment: null,
        cancellable: false,
      });
    }

    // Rendez-vous de suivi. Leur numéro visible continue après le rendez-vous
    // initial, tandis que l'ordinal serveur reste réservé au quota de suivi.
    (Array.isArray(consultationsLog) ? consultationsLog : []).forEach((c, idx) => {
      const ts = toTs(c.consultedAt || c.created_at || c.createdAt || c.date);
      if (ts == null) return;
      const num = idx + (initialAppointmentCompleted ? 2 : 1);
      const appointment = readExactAppointmentV2(c?.clinical?.appointment);
      const answeredCount = appointment?.questions.filter((question) => question.answer).length || 0;
      const longitudinalConsultation = longitudinalRows.find(
        (event) => event?.kind === 'consultation' && event?.source === c,
      );
      const reportSub = c.notes
        ? String(c.notes).trim()
        : (appointment ? `${answeredCount} réponse${answeredCount > 1 ? 's' : ''} dans le compte rendu` : null);
      out.push({
        id: `consult-${num}`,
        kind: 'consultation',
        ts,
        num,
        label: `Rendez-vous n°${num}`,
        sub: [reportSub, longitudinalConsultation?.sub]
          .filter((value, index, values) => value && values.indexOf(value) === index)
          .join(' · ') || null,
        appointment,
        cancellable: idx === consultationsLog.length - 1
          && Boolean(appointment)
          && !dependentAppointmentShas.has(appointment.sha256),
      });
    });

    // Versions du protocole (versions[0] = plus récente) + publications.
    list.forEach((v, i) => {
      const n = list.length - i;
      const ts = toTs(v.createdAt || v.created_at);
      if (ts != null) {
        out.push({
          id: `version-${n}`,
          kind: 'version',
          ts,
          label: v.label || (n === 1 ? 'Protocole initial — V1' : `Suite du protocole — V${n}`),
          // Une V déjà publiée n'est plus « à valider », même si son statut de
          // consultation est resté 'a_valider' (le statut ne repasse pas à la
          // publication). On se base sur la publication réelle.
          chip: i === 0
            ? ((v.status === 'a_valider' && !v.client_app_publication?.published_at) ? 'à valider' : 'active')
            : null,
          chipTone: (i === 0 && v.status === 'a_valider' && !v.client_app_publication?.published_at) ? 'warn' : 'accent',
          sub: v.paperGenerated ? 'envoyée en papier' : null,
          version: v,
        });
      }
      const pub = v.client_app_publication || null;
      const pubTs = toTs(pub?.published_at);
      if (pubTs != null) {
        out.push({
          id: `publication-${n}`,
          kind: 'publication',
          ts: pubTs,
          label: `V${n} publiée dans l'app`,
        });
      }
    });

    // Transitions de phase (phases terminées du parcours thérapeutique).
    (protocolPhases?.phases || [])
      .filter((p) => p.status === 'completed' && p.completed_at)
      .forEach((p, idx) => {
        const ts = toTs(p.completed_at);
        if (ts == null) return;
        out.push({
          id: `phase-${p.id || p.order}-${idx}`,
          kind: 'phase',
          ts,
          label: `Fin de phase ${p.order} — ${p.client_name}`,
        });
      });

    // Ressentis groupés par semaine (même esprit que les dots hebdo).
    const fbBuckets = new Map();
    (Array.isArray(feedbacks) ? feedbacks : []).forEach((f) => {
      const ts = toTs(f.updated_at || f.created_at || f.date);
      if (ts == null) return;
      const wk = weekStart(ts);
      const b = fbBuckets.get(wk) || { count: 0, latest: 0 };
      b.count += 1;
      if (ts > b.latest) b.latest = ts;
      fbBuckets.set(wk, b);
    });
    fbBuckets.forEach((b, wk) => {
      out.push({
        id: `fb-${wk}`,
        kind: 'feedback',
        ts: b.latest,
        label: b.count === 1 ? 'Ressenti reçu' : `${b.count} ressentis reçus`,
        sub: `semaine du ${fmtDay(wk)}`,
      });
    });

    // Le parent transmet uniquement les bilans liés au jalon exact et à la
    // période courante. La frise affiche leur statut sans analyser les réponses.
    (Array.isArray(cycleReviews) ? cycleReviews : []).forEach((review) => {
      if (review?.status !== 'sent' && review?.status !== 'submitted') return;
      if (linkedCycleReviews.has(cycleReviewKey(review))) return;
      const ts = toTs(review.status === 'submitted' ? review.submitted_at : review.created_at);
      if (ts == null || !review.id) return;
      const received = review.status === 'submitted';
      out.push({
        id: `cycle-review-${review.id}`,
        kind: 'cycle-review',
        ts,
        label: `${review.cycle_label || 'Bilan de cycle'} ${received ? 'reçu' : 'envoyé'}`,
        chip: received ? 'reçu' : 'en attente',
        chipTone: received ? 'accent' : 'warn',
      });
    });

    // Jalons poids : dernière pesée de chaque semaine + delta vs semaine
    // précédente affichée (lecture seule, aucun seuil).
    const wBuckets = new Map();
    (Array.isArray(weightEntries) ? weightEntries : []).forEach((w) => {
      const ts = toTs(w.date);
      if (ts == null || typeof w.weight_kg !== 'number') return;
      const wk = weekStart(ts);
      const prev = wBuckets.get(wk);
      if (!prev || ts > prev.ts) wBuckets.set(wk, { ts, kg: w.weight_kg });
    });
    const wRows = Array.from(wBuckets.entries())
      .map(([wk, b]) => ({ wk, ...b }))
      .sort((a, b) => b.wk - a.wk);
    wRows.forEach((row, i) => {
      const older = wRows[i + 1];
      const delta = older ? Number((row.kg - older.kg).toFixed(1)) : null;
      out.push({
        id: `weight-${row.wk}`,
        kind: 'weight',
        ts: row.ts,
        delta,
        label: `Pesée · ${row.kg} kg`,
        sub: delta != null && delta !== 0 ? `${delta > 0 ? '+' : ''}${delta} kg vs semaine préc.` : null,
      });
    });

    // Publications en attente / programmées (data buildPublicationMarkers —
    // qui garantit toujours une date ; on saute un marqueur illisible plutôt
    // que d'inventer un « maintenant »).
    (Array.isArray(publicationMarkers) ? publicationMarkers : []).forEach((m, i) => {
      const ts = toTs(m.date);
      if (ts == null) return;
      out.push({
        id: `pubmark-${m.kind}-${i}`,
        kind: m.kind === 'scheduled' ? 'pub-scheduled' : 'pub-pending',
        ts,
        label: m.label,
      });
    });

    // Les repas et échanges sont les deux nouveaux apports de la lecture
    // longitudinale. Les autres types restent construits ci-dessus afin de
    // préserver les actions et détails historiques de la frise existante.
    longitudinalRows
      .filter((event) => event?.kind === 'meals' || event?.kind === 'messages')
      .forEach((event) => {
        if (!Number.isFinite(event?.ts) || !event?.id || !event?.label) return;
        out.push({
          id: `longitudinal-${event.id}`,
          kind: event.kind,
          ts: event.ts,
          label: event.label,
          sub: event.sub || null,
        });
      });

    const bounds = periodBounds(period);
    const bounded = bounds === null
      ? out
      : (bounds === false ? [] : out.filter((event) => event.ts >= bounds.from && event.ts <= bounds.to));

    // Anti-chronologique : récent en haut ; à date égale, le plus décisionnel.
    bounded.sort((a, b) => (b.ts - a.ts) || ((KIND_RANK[b.kind] || 0) - (KIND_RANK[a.kind] || 0)));
    return bounded;
  }, [cycleReviews, deliveredAt, initialAppointmentAt, initialAppointmentCompleted, consultationsLog, feedbacks, versions, weightEntries, protocolPhases, publicationMarkers, longitudinalEvents, period]);

  const pub = activePlanPublication && activePlanPublication.state !== 'unknown'
    ? activePlanPublication
    : null;

  return (
    <div className="jrn-block jrn-vtl">
      <div className="jrn-block__head">
        <h3 className="jrn-block__title">La vie du suivi</h3>
        <div className="jrn-block__head-meta">
          {/* Statut de publication du plan actif — masqué si inconnu (on
              n'affirme jamais « visible » sans preuve, règle fail-closed). */}
          {pub && (
            <span
              className={`jrn-vtl__pub jrn-vtl__pub--${pub.tone}`}
              title={pub.reason || undefined}
            >
              {pub.label}
              {(() => { const t = toTs(pub.date); return t != null ? ` · ${fmtDay(t)}` : ''; })()}
            </span>
          )}
          <span className="jrn-meta-chip jrn-meta-chip--neutral">
            {events.length} évènement{events.length > 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {events.length === 0 ? (
        <p className="jrn-cockpit-empty-row">
          Aucun évènement pour l'instant — le journal se remplit avec les premiers ressentis et consultations.
        </p>
      ) : (
        <div className="jrn-vtl__scroll">
          <ol className="jrn-vtl__list">
            {events.map((ev) => (
              <li key={ev.id} className={`jrn-vtl__item jrn-vtl__item--${ev.kind}`}>
                <span className="jrn-vtl__node">
                  {ev.kind === 'consultation' ? ev.num : <Glyph kind={ev.kind} delta={ev.delta} />}
                </span>
                <span className="jrn-vtl__date">{fmtDay(ev.ts)}</span>
                <div className="jrn-vtl__body">
                  <div className="jrn-vtl__label">
                    {ev.label}
                    {ev.chip && (
                      <span className={`jrn-vtl__chip jrn-vtl__chip--${ev.chipTone || 'accent'}`}>
                        {ev.chip}
                      </span>
                    )}
                  </div>
                  {ev.sub && (
                    <div className="jrn-vtl__sub" title={ev.sub}>{ev.sub}</div>
                  )}
                  {ev.appointment && (
                    <details className="jrn-vtl__report">
                      <summary>Voir le compte rendu</summary>
                      {ev.appointment.summary && (
                        <p className="jrn-vtl__report-summary">{ev.appointment.summary}</p>
                      )}
                      {ev.appointment.questions.length > 0 && (
                        <dl>
                          {ev.appointment.questions.map((question, index) => (
                            <div key={`${ev.id}-question-${index}`}>
                              <dt>{question.text}</dt>
                              <dd>{question.answer || 'Réponse non renseignée.'}</dd>
                            </div>
                          ))}
                        </dl>
                      )}
                    </details>
                  )}
                </div>
                {ev.version && typeof onPreviewVersion === 'function' && (
                  <button
                    type="button"
                    className="jrn-vtl__btn"
                    onClick={() => onPreviewVersion(ev.version)}
                  >
                    Voir
                  </button>
                )}
                {ev.cancellable && typeof onRemoveLastConsultation === 'function' && (
                  <button
                    type="button"
                    className="jrn-vtl__btn"
                    onClick={onRemoveLastConsultation}
                    disabled={busy}
                    title="Annuler la dernière consultation enregistrée (clic accidentel)"
                  >
                    Annuler
                  </button>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
